import { fontAttrs } from "@/components/tools/printables/page-kit";
import {
  type CivilDate,
  monthName,
  shortDate,
  type WeekStart,
  weekdayNames,
} from "@/lib/tool-kit/printables/calendar";
import type {
  MiniMonth,
  MonthPage,
  YearPage,
} from "@/lib/tool-kit/printables/calendar-pages";
import {
  isHoliday,
  type LegendEntry,
  labelsFor,
} from "@/lib/tool-kit/printables/holidays";
import { r2 } from "@/lib/tool-kit/printables/page";
import {
  type FontSpec,
  fitLines,
  type TextMeasure,
  truncateToFit,
} from "@/lib/tool-kit/printables/text";

const INK = "#1a1a1a";
const MUTED = "#8a8a8a";
const FAINT = "#b5b5b5";
const RULE = "#9a9a9a";
const WEEKEND = "#f0f0f0";
const SANS: FontSpec = { kind: "sans" };
const BOLD: FontSpec = { kind: "sans", bold: true };

export interface CalendarStyle {
  locale: string;
  weekStart: WeekStart;
  accent: string;
  shadeWeekends: boolean;
  showOutside: boolean;
  holidays: ReadonlyMap<string, string[]>;
}

function date(cell: CivilDate): CivilDate {
  return { year: cell.year, month: cell.month, day: cell.day };
}

/** One month filling a page. */
export function MonthArt({
  page,
  style,
  title,
  weekLabel,
  notesLabel,
  measure,
  left,
  right,
}: {
  page: MonthPage;
  style: CalendarStyle;
  /** The user's own title, shown at the right of the title band. */
  title: string;
  weekLabel: string;
  notesLabel: string;
  measure: TextMeasure;
  left: number;
  right: number;
}) {
  const names = weekdayNames(style.locale, style.weekStart, "long");
  const heading = `${monthName(style.locale, page.month)} ${page.year}`;
  return (
    <g>
      <text
        fill={style.accent}
        fontSize={13}
        x={r2(left)}
        y={page.titleBaseline}
        {...fontAttrs(BOLD)}
      >
        {heading}
      </text>
      {title ? (
        <text
          fill={MUTED}
          fontSize={5}
          textAnchor="end"
          x={r2(right)}
          y={page.titleBaseline}
          {...fontAttrs(SANS)}
        >
          {title}
        </text>
      ) : null}
      {page.header.map((box, index) => (
        <g key={box.x}>
          <rect
            fill={style.accent}
            height={box.height}
            opacity={0.14}
            width={box.width}
            x={box.x}
            y={box.y}
          />
          <text
            fill={INK}
            fontSize={3.6}
            textAnchor="middle"
            x={r2(box.x + box.width / 2)}
            y={r2(box.y + 5.4)}
            {...fontAttrs(BOLD)}
          >
            {names[index]}
          </text>
        </g>
      ))}
      {page.weekColumn ? (
        <text
          fill={MUTED}
          fontSize={3}
          textAnchor="middle"
          x={r2(page.weekColumn.x + page.weekColumn.width / 2)}
          y={r2((page.header[0]?.y ?? 0) + 5.2)}
          {...fontAttrs(SANS)}
        >
          {weekLabel}
        </text>
      ) : null}
      {page.rows.map((row) => (
        <g key={row.box.y}>
          {page.weekColumn ? (
            <text
              fill={MUTED}
              fontSize={3.2}
              textAnchor="middle"
              x={r2(page.weekColumn.x + page.weekColumn.width / 2)}
              y={r2(row.box.y + 5)}
              {...fontAttrs(SANS)}
            >
              {row.isoWeek}
            </text>
          ) : null}
          {row.cells.map((cell) => (
            <DayBox
              cell={cell}
              key={`${cell.month}-${cell.day}`}
              measure={measure}
              style={style}
            />
          ))}
        </g>
      ))}
      <rect
        fill="none"
        height={page.grid.height}
        stroke={INK}
        strokeWidth={0.4}
        width={page.grid.width}
        x={page.grid.x}
        y={page.grid.y}
      />
      {page.notes ? <Notes box={page.notes} label={notesLabel} /> : null}
    </g>
  );
}

function DayBox({
  cell,
  style,
  measure,
}: {
  cell: MonthPage["rows"][number]["cells"][number];
  style: CalendarStyle;
  measure: TextMeasure;
}) {
  const { box } = cell;
  const marked = cell.inMonth && isHoliday(style.holidays, date(cell));
  const labels = cell.inMonth
    ? labelsFor(style.holidays, date(cell)).filter(Boolean)
    : [];
  const note =
    labels.length > 0
      ? fitLines(measure, labels.join("; "), SANS, box.width - 3, 3, 2.2, 2)
      : null;
  let fill = "none";
  if (marked) {
    fill = style.accent;
  } else if (cell.weekend && style.shadeWeekends) {
    fill = WEEKEND;
  }
  const showNumber = cell.inMonth || style.showOutside;
  return (
    <g>
      <rect
        fill={fill}
        height={box.height}
        opacity={marked ? 0.16 : 1}
        stroke={RULE}
        strokeWidth={0.25}
        width={box.width}
        x={box.x}
        y={box.y}
      />
      {showNumber ? (
        <text
          fill={cell.inMonth ? INK : FAINT}
          fontSize={cell.inMonth ? 4.6 : 3.6}
          x={r2(box.x + 1.8)}
          y={r2(box.y + 5)}
          {...fontAttrs(cell.inMonth ? BOLD : SANS)}
        >
          {cell.day}
        </text>
      ) : null}
      {note?.lines.map((line, index) => (
        <text
          fill={INK}
          fontSize={note.size}
          key={line}
          x={r2(box.x + 1.8)}
          y={r2(box.y + 9.6 + index * note.size * 1.2)}
          {...fontAttrs(SANS)}
        >
          {line}
        </text>
      ))}
    </g>
  );
}

function Notes({
  box,
  label,
}: {
  box: { x: number; y: number; width: number; height: number };
  label: string;
}) {
  const lines: number[] = [];
  for (let y = box.y + 12; y < box.y + box.height - 1; y += 7) {
    lines.push(r2(y));
  }
  return (
    <g>
      <rect
        fill="none"
        height={box.height}
        rx={1.5}
        stroke={INK}
        strokeWidth={0.35}
        width={box.width}
        x={box.x}
        y={box.y}
      />
      <text
        fill={MUTED}
        fontSize={3.6}
        x={r2(box.x + 2)}
        y={r2(box.y + 5)}
        {...fontAttrs(BOLD)}
      >
        {label}
      </text>
      {lines.map((y) => (
        <line
          key={y}
          stroke={RULE}
          strokeWidth={0.25}
          x1={r2(box.x + 2)}
          x2={r2(box.x + box.width - 2)}
          y1={y}
          y2={y}
        />
      ))}
    </g>
  );
}

function Mini({
  mini,
  style,
  showYear,
}: {
  mini: MiniMonth;
  style: CalendarStyle;
  showYear: boolean;
}) {
  const letters = weekdayNames(style.locale, style.weekStart, "narrow");
  const name = monthName(style.locale, mini.month);
  const heading = showYear ? `${name} ${mini.year}` : name;
  return (
    <g>
      <text
        fill={style.accent}
        fontSize={4.4}
        x={mini.box.x}
        y={mini.nameBaseline}
        {...fontAttrs(BOLD)}
      >
        {heading}
      </text>
      <line
        stroke={style.accent}
        strokeWidth={0.4}
        x1={mini.box.x}
        x2={r2(mini.box.x + mini.box.width)}
        y1={r2(mini.nameBaseline + 1.2)}
        y2={r2(mini.nameBaseline + 1.2)}
      />
      {mini.header.map((box, index) => (
        <text
          fill={MUTED}
          fontSize={2.5}
          key={box.x}
          textAnchor="middle"
          x={r2(box.x + box.width / 2)}
          y={r2(box.y + 3.6)}
          {...fontAttrs(SANS)}
        >
          {letters[index]}
        </text>
      ))}
      {mini.rows.map((row) => (
        <g key={row.box.y}>
          {mini.weekColumn ? (
            <text
              fill={FAINT}
              fontSize={2}
              textAnchor="middle"
              x={r2(mini.weekColumn.x + mini.weekColumn.width / 2)}
              y={r2(row.box.y + row.box.height * 0.68)}
              {...fontAttrs(SANS)}
            >
              {row.isoWeek}
            </text>
          ) : null}
          {row.cells
            .filter((cell) => cell.inMonth)
            .map((cell) => {
              const marked = isHoliday(style.holidays, date(cell));
              const size = Math.min(3, cell.box.height * 0.62);
              let fill = INK;
              if (cell.weekend && style.shadeWeekends) {
                fill = MUTED;
              }
              return (
                <g key={cell.day}>
                  {marked ? (
                    <rect
                      fill={style.accent}
                      height={r2(cell.box.height * 0.86)}
                      opacity={0.28}
                      rx={0.8}
                      width={r2(cell.box.width * 0.92)}
                      x={r2(cell.box.x + cell.box.width * 0.04)}
                      y={r2(cell.box.y + cell.box.height * 0.07)}
                    />
                  ) : null}
                  <text
                    fill={fill}
                    fontSize={r2(size)}
                    textAnchor="middle"
                    x={r2(cell.box.x + cell.box.width / 2)}
                    y={r2(cell.box.y + cell.box.height * 0.68)}
                    {...fontAttrs(SANS)}
                  >
                    {cell.day}
                  </text>
                </g>
              );
            })}
        </g>
      ))}
    </g>
  );
}

/** A year (or academic year) of twelve small months, with the marked days listed below. */
export function YearArt({
  page,
  style,
  heading,
  title,
  showYearInNames,
  legend,
  legendMore,
  measure,
  left,
  right,
}: {
  page: YearPage;
  style: CalendarStyle;
  heading: string;
  title: string;
  showYearInNames: boolean;
  legend: readonly LegendEntry[];
  /** "+ 3 more", when the list was cut. */
  legendMore: string;
  measure: TextMeasure;
  left: number;
  right: number;
}) {
  return (
    <g>
      <text
        fill={style.accent}
        fontSize={11}
        x={r2(left)}
        y={page.titleBaseline}
        {...fontAttrs(BOLD)}
      >
        {heading}
      </text>
      {title ? (
        <text
          fill={MUTED}
          fontSize={5}
          textAnchor="end"
          x={r2(right)}
          y={page.titleBaseline}
          {...fontAttrs(SANS)}
        >
          {title}
        </text>
      ) : null}
      {page.months.map((mini) => (
        <Mini
          key={`${mini.year}-${mini.month}`}
          mini={mini}
          showYear={showYearInNames}
          style={style}
        />
      ))}
      {page.legend ? (
        <Legend
          box={page.legend}
          entries={legend}
          measure={measure}
          more={legendMore}
          style={style}
        />
      ) : null}
    </g>
  );
}

const LEGEND_COLUMNS = 3;
const LEGEND_ROW = 4.6;

function Legend({
  box,
  entries,
  more,
  style,
  measure,
}: {
  box: { x: number; y: number; width: number; height: number };
  entries: readonly LegendEntry[];
  more: string;
  style: CalendarStyle;
  measure: TextMeasure;
}) {
  const perColumn = Math.max(1, Math.floor(box.height / LEGEND_ROW));
  const capacity = perColumn * LEGEND_COLUMNS;
  const shown = entries.slice(
    0,
    entries.length > capacity ? capacity - 1 : capacity
  );
  const width = box.width / LEGEND_COLUMNS - 3;
  const items = shown.map((entry) => {
    const same =
      entry.start.day === entry.end.day &&
      entry.start.month === entry.end.month &&
      entry.start.year === entry.end.year;
    const dates = same
      ? shortDate(style.locale, entry.start)
      : `${shortDate(style.locale, entry.start)} – ${shortDate(style.locale, entry.end)}`;
    return entry.label ? `${dates}  ${entry.label}` : dates;
  });
  if (entries.length > capacity) {
    items.push(more);
  }
  return (
    <g>
      <line
        stroke={RULE}
        strokeWidth={0.3}
        x1={box.x}
        x2={r2(box.x + box.width)}
        y1={box.y}
        y2={box.y}
      />
      {items.map((item, index) => (
        <text
          fill={INK}
          fontSize={3}
          key={`${index}-${item}`}
          x={r2(
            box.x + Math.floor(index / perColumn) * (box.width / LEGEND_COLUMNS)
          )}
          y={r2(box.y + 5 + (index % perColumn) * LEGEND_ROW)}
          {...fontAttrs(SANS)}
        >
          {truncateToFit(measure, item, SANS, 3, width)}
        </text>
      ))}
    </g>
  );
}
